// src/components/QrValidator.tsx — validação de ingressos na portaria.
// Lê QR com a câmera (html5-qrcode) e confere contra a lista já carregada
// pelo admin (POST /list). QR = https://espacoera.store/validate/{ticket_number}.
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface Props {
  ingressos: any[];
}

type Resultado =
  | { kind: 'aprovado'; ticket: string; ingresso: any }
  | { kind: 'pendente'; ticket: string; ingresso: any }
  | { kind: 'nao-encontrado'; ticket: string };

export function extractTicketNumber(text: string): string | null {
  const t = (text || '').trim();
  if (!t) return null;
  const mValidate = t.match(/validate\/(\d+)/);
  if (mValidate) return mValidate[1];
  const mLong = t.match(/(\d{10,16})/);
  if (mLong) return mLong[1];
  if (/^\d+$/.test(t)) return t;
  return null;
}

const QrValidator: React.FC<Props> = ({ ingressos }) => {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [scanId, setScanId] = useState(0);
  const [flash, setFlash] = useState<{ kind: Resultado['kind']; id: number } | null>(null);
  const lastTicketRef = useRef<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  const validar = (raw: string) => {
    const ticket = extractTicketNumber(raw);
    if (!ticket) {
      setError('QR lido, mas não contém um número de ingresso válido.');
      return;
    }
    // Leitura contínua: a câmera dispara o callback a cada frame com o mesmo
    // QR em vista — ignora sem re-render nem flash até vir um ticket novo.
    // A câmera permanece aberta para ler vários em sequência.
    if (ticket === lastTicketRef.current) return;
    const achado = ingressos.find((i) => String(i.ticket_number) === ticket);
    setError('');
    const res: Resultado = !achado
      ? { kind: 'nao-encontrado', ticket }
      : achado.payment_approved
        ? { kind: 'aprovado', ticket, ingresso: achado }
        : { kind: 'pendente', ticket, ingresso: achado };
    lastTicketRef.current = ticket;
    setResultado(res);
    setScanId((n) => n + 1);
    setFlash({ kind: res.kind, id: Date.now() });
    try {
      navigator.vibrate?.(res.kind === 'aprovado' ? 60 : [100, 50, 100]);
    } catch {
      /* sem vibração */
    }
  };

  const stopScanner = async () => {
    try {
      const s = scannerRef.current;
      if (s) {
        try {
          if (s.isScanning) await s.stop();
        } catch {
          /* já parado */
        }
        try {
          s.clear();
        } catch {
          /* sem UI p/ limpar */
        }
      }
    } finally {
      scannerRef.current = null;
      if (openRef.current) setScanning(false);
    }
  };

  const startScanner = async () => {
    setError('');
    setResultado(null);
    lastTicketRef.current = null; // nova sessão de leitura: próximo QR sempre anima
    // Exige contexto seguro (https/localhost); em http a câmera é bloqueada.
    if (window.isSecureContext === false) {
      setError('A câmera exige HTTPS. Use https://espacoera.store/admin ou digite o número manualmente.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador não suporta câmera. Digite o número do ingresso manualmente.');
      return;
    }
    setScanning(true);
    try {
      // Aguarda o <div id="era-qr-reader"> existir após o setState.
      await new Promise((r) => setTimeout(r, 50));
      const el = document.getElementById('era-qr-reader');
      if (!el) throw new Error('Região de leitura indisponível.');
      const scanner = new Html5Qrcode('era-qr-reader');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decoded) => {
          validar(decoded);
        },
        () => {
          /* ignora frames sem QR */
        }
      );
    } catch (e: any) {
      console.error('QR start error:', e);
      const msg = String(e?.message || e || '');
      if (/permission|NotAllowed/i.test(msg)) {
        setError('Permissão de câmera negada. Libere o acesso ou digite o número manualmente.');
      } else if (/NotFound|no camera|device/i.test(msg)) {
        setError('Nenhuma câmera encontrada. Digite o número do ingresso manualmente.');
      } else {
        setError('Não foi possível abrir a câmera. Digite o número do ingresso manualmente.');
      }
      await stopScanner();
    }
  };

  useEffect(() => {
    if (!open) {
      stopScanner();
    }
  }, [open ]);

  // Flash de válido/inválido some sozinho após a animação.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    return () => {
      openRef.current = false;
      const s = scannerRef.current;
      if (s) {
        try {
          if (s.isScanning) s.stop().catch(() => undefined);
        } catch {
          /* unmount */
        }
        try {
          s.clear();
        } catch {
          /* unmount */
        }
      }
      scannerRef.current = null;
    };
  }, []);

  const handleManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (manual.trim()) validar(manual);
  };

  return (
    <section className="era-admin-qr" aria-label="Validação de ingressos por QR">
      {!open ? (
        <button
          type="button"
          className="era-primary-btn era-admin-submit"
          onClick={() => setOpen(true)}
        >
          Ler QR code
        </button>
      ) : (
        <div className="era-admin-qr-panel">
          <div className="era-admin-qr-head">
            <h2 className="era-admin-qr-title">Validar ingresso</h2>
            <button
              type="button"
              className="era-cta-line"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>

          {!scanning ? (
            <button
              type="button"
              className="era-admin-resend"
              onClick={startScanner}
            >
              {resultado ? 'Ler próximo ingresso' : 'Abrir câmera'}
            </button>
          ) : (
            <button
              type="button"
              className="era-cta-line"
              onClick={stopScanner}
            >
              Parar câmera
            </button>
          )}

          <div id="era-qr-reader" className="era-admin-qr-video" aria-label="Visor da câmera" />

          <form className="era-admin-qr-manual" onSubmit={handleManual}>
            <label className="era-admin-label" htmlFor="qrManual">
              Ou digite o número do ingresso
            </label>
            <div className="era-admin-qr-manual-row">
              <input
                id="qrManual"
                className="era-admin-input"
                type="text"
                inputMode="numeric"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="Ex.: 1757884800000"
              />
              <button type="submit" className="era-admin-resend">
                Validar
              </button>
            </div>
          </form>

          {error && (
            <p className="era-admin-error" role="alert">{error}</p>
          )}

          {resultado && (
            <div
              key={scanId}
              className={`era-admin-qr-result is-${resultado.kind} is-anim`}
              role="status"
              aria-live="polite"
            >
              {resultado.kind === 'aprovado' && (
                <>
                  <p className="era-admin-qr-result-title">Ingresso válido — entrada liberada</p>
                  <p className="era-admin-qr-result-line">
                    Ticket {resultado.ticket} · {resultado.ingresso.nome} · {resultado.ingresso.tipo_ingresso}
                  </p>
                </>
              )}
              {resultado.kind === 'pendente' && (
                <>
                  <p className="era-admin-qr-result-title">Pagamento pendente — não liberar</p>
                  <p className="era-admin-qr-result-line">
                    Ticket {resultado.ticket} · {resultado.ingresso.nome} · {resultado.ingresso.email}
                  </p>
                </>
              )}
              {resultado.kind === 'nao-encontrado' && (
                <>
                  <p className="era-admin-qr-result-title">Ingresso não encontrado</p>
                  <p className="era-admin-qr-result-line">
                    Ticket {resultado.ticket} não está na lista deste evento.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {flash && (
        <div key={flash.id} className={`era-qr-flash is-${flash.kind}`} aria-hidden="true">
          <span className="era-qr-flash-icon">{flash.kind === 'aprovado' ? '✓' : '✕'}</span>
          <span className="era-qr-flash-label">
            {flash.kind === 'aprovado' ? 'Válido' : flash.kind === 'pendente' ? 'Pendente' : 'Inválido'}
          </span>
        </div>
      )}
    </section>
  );
};

export default QrValidator;
